import React, { useMemo } from 'react';
import { useListContext, useGetOne } from 'react-admin';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { getCurrencySymbol } from '@src/utils/currency';
import type { PartnerSettings } from '@src/settings';
import type { YAdjustedRowWithId } from './types';

import type { ItemAdjustedPricesFilterValues } from './ItemAdjustedPricesFilters';

const formatCurrency = (value: number | null | undefined, symbol: string): string => {
  if (value == null) return '-';
  return `${symbol}${(value / 100).toFixed(2)}`;
};

interface ItemAdjustedPricesTableProps {
  filters: ItemAdjustedPricesFilterValues;
}

const ItemAdjustedPricesTable = ({ filters }: ItemAdjustedPricesTableProps) => {
  const { data, isPending } = useListContext<YAdjustedRowWithId>();
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencySymbol = getCurrencySymbol(settings?.primaryCurrencyCode);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((row) => {
      if (filters.parentSku && !(row.parentSku ?? '').toLowerCase().includes(filters.parentSku.toLowerCase())) return false;
      if (filters.skuSubcode && !(row.skuSubcode ?? '').toLowerCase().includes(filters.skuSubcode.toLowerCase())) return false;
      if (filters.color && !(row.color ?? '').toLowerCase().includes(filters.color.toLowerCase())) return false;
      if (filters.yadjustedCode && !(row.yadjustedCode ?? '').toLowerCase().includes(filters.yadjustedCode.toLowerCase())) return false;
      if (filters.sizeCode && !(row.sizeCode ?? '').toLowerCase().includes(filters.sizeCode.toLowerCase())) return false;
      if (filters.department && row.department !== filters.department) return false;
      if (filters.category && row.category !== filters.category) return false;
      return true;
    });
  }, [data, filters]);

  if (isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No y-adjusted pricing data found.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mx: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Parent SKU</TableCell>
            <TableCell>SKU Subcode</TableCell>
            <TableCell>Color</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Yerdle Price</TableCell>
            <TableCell>Y-Adjusted Price</TableCell>
            <TableCell>Size Code</TableCell>
            <TableCell sx={{ width: '15%' }}>Department</TableCell>
            <TableCell sx={{ width: '15%' }}>Category</TableCell>
            <TableCell sx={{ width: '20%' }}>Title</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.map(row => (
            <TableRow key={row.id} hover>
              <TableCell>{row.parentSku ?? '-'}</TableCell>
              <TableCell>{row.skuSubcode ?? '-'}</TableCell>
              <TableCell>{row.color ?? '-'}</TableCell>
              <TableCell>{row.yadjustedCode ?? '-'}</TableCell>
              <TableCell>{formatCurrency(row.price, currencySymbol)}</TableCell>
              <TableCell>{formatCurrency(row.yerdlePrice, currencySymbol)}</TableCell>
              <TableCell>{formatCurrency(row.yadjustedPrice, currencySymbol)}</TableCell>
              <TableCell>{row.sizeCode ?? '-'}</TableCell>
              <TableCell>{row.department ?? '-'}</TableCell>
              <TableCell>{row.category ?? '-'}</TableCell>
              <TableCell>{row.title ?? '-'}</TableCell>
            </TableRow>
          ))}
          {filteredData.length === 0 && data.length > 0 && (
            <TableRow>
              <TableCell colSpan={11} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No rows match the current filters on this page.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ItemAdjustedPricesTable;
